/* ss-cart-drawer.js — SeatShield custom cart drawer (vanilla JS, no jQuery) */

(function () {
  'use strict';

  const SECTION_ID = 'ss-cart-drawer';
  const POPUP_SECTION_ID = 'ss-checkout-popup';

  // After every SRA response, pass the popup section HTML to the popup JS so
  // it can update config.cart_state (which includes collection memberships that
  // /cart.js doesn't expose).
  function syncPopupCartState(sectionsMap) {
    var html = sectionsMap && sectionsMap[POPUP_SECTION_ID];
    if (html && window.SsCheckoutPopup && typeof window.SsCheckoutPopup.refreshCartState === 'function') {
      window.SsCheckoutPopup.refreshCartState(html);
    }
  }
  const CONFETTI_TIER1_KEY = 'ss_confetti_tier1_shown';
  const CONFETTI_TIER2_KEY = 'ss_confetti_tier2_shown';
  const REWARDS_DEFAULT_APPLIED_KEY = 'ss_rewards_default_applied';

  /* ── DOM refs ───────────────────────────────────────────────────────────── */
  let drawer, overlay, announcerEl;

  function contentsEl() {
    return document.getElementById('SsCartDrawerContents');
  }

  /* ── Init ───────────────────────────────────────────────────────────────── */
  function init() {
    drawer = document.getElementById('SsCartDrawer');
    if (!drawer) return;

    overlay = document.getElementById('SsCartOverlay');
    announcerEl = document.getElementById('SsCartAnnouncement');

    bindEvents();
    handleUrlParam();
    applyRewardsDefault();
  }

  /* ── Open / close ───────────────────────────────────────────────────────── */
  function openDrawer() {
    drawer.classList.add('ss-cart-drawer--open');
    overlay && overlay.classList.add('ss-overlay--visible');
    document.body.style.overflow = 'hidden';
    drawer.removeAttribute('aria-hidden');
    trapFocus(drawer);
  }

  function closeDrawer() {
    drawer.classList.remove('ss-cart-drawer--open');
    overlay && overlay.classList.remove('ss-overlay--visible');
    document.body.style.overflow = '';
    drawer.setAttribute('aria-hidden', 'true');
    releaseFocus();
  }

  /* ── Focus trap ─────────────────────────────────────────────────────────── */
  let _trapHandler = null;
  let _returnFocus = null;

  function trapFocus(el) {
    _returnFocus = document.activeElement;
    const foc = focusable(el);
    if (foc.length) foc[0].focus();
    _trapHandler = function (e) {
      if (e.key !== 'Tab') return;
      const f = focusable(el);
      if (!f.length) return;
      if (e.shiftKey) {
        if (document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
      } else {
        if (document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
      }
    };
    document.addEventListener('keydown', _trapHandler);
  }

  function releaseFocus() {
    if (_trapHandler) {
      document.removeEventListener('keydown', _trapHandler);
      _trapHandler = null;
    }
    if (_returnFocus && typeof _returnFocus.focus === 'function') {
      _returnFocus.focus();
      _returnFocus = null;
    }
  }

  function focusable(el) {
    return Array.from(el.querySelectorAll(
      'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])'
    )).filter(function (n) { return n.offsetParent !== null; });
  }

  /* ── Event bindings ─────────────────────────────────────────────────────── */
  function bindEvents() {
    // Cart icon click — capture phase so we beat Impulse's own listener
    document.addEventListener('click', function (e) {
      if (e.target.closest('.js-drawer-open-cart, [data-ss-cart-open]')) {
        e.preventDefault();
        e.stopPropagation();
        openDrawer();
      }
    }, true);

    // Close button / overlay / ESC
    document.addEventListener('click', function (e) {
      if (e.target.closest('#SsCartDrawer .ss-cart-drawer__close, [data-ss-cart-close]')) closeDrawer();
    });
    overlay && overlay.addEventListener('click', closeDrawer);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('ss-cart-drawer--open')) closeDrawer();
    });

    // Delegated drawer actions
    document.addEventListener('click', handleClick);
    document.addEventListener('change', handleChange);

    // Intercept add-to-cart form submissions globally — CAPTURE phase.
    // stopImmediatePropagation() ensures no other submit listener (theme
    // analytics or third-party scripts) fires on the same event.
    // If a future SS section needs to react to cart-add submits, dispatch a
    // custom event from inside handleFormSubmit instead of adding another
    // listener — that way ordering stays under our control.
    document.addEventListener('submit', handleFormSubmit, true);

    // Custom event hook for other code to trigger a cart refresh
    document.addEventListener('ss:cart:updated', function () { refreshCart(); });
  }

  /* ── Click delegation ───────────────────────────────────────────────────── */
  function handleClick(e) {
    if (!drawer) return;

    // Upsell variant button (not a data-action, just selection state)
    const variantBtn = e.target.closest('.ss-upsell-card__variant-btn');
    if (variantBtn && drawer.contains(variantBtn)) {
      const card = variantBtn.closest('.ss-upsell-card');
      card.querySelectorAll('.ss-upsell-card__variant-btn').forEach(function (b) {
        b.classList.remove('ss-upsell-card__variant-btn--selected');
      });
      variantBtn.classList.add('ss-upsell-card__variant-btn--selected');
      const addBtn = card.querySelector('[data-action="upsell-add"]');
      if (addBtn) addBtn.dataset.variantId = variantBtn.dataset.variantId;
      return;
    }

    const btn = e.target.closest('[data-action]');
    if (!btn || !drawer.contains(btn)) return;

    switch (btn.dataset.action) {
      case 'remove-item':
        cartChange(btn.dataset.key, 0);
        break;
      case 'decrement':
        cartChange(btn.dataset.key, Math.max(0, parseInt(btn.dataset.qty, 10)));
        break;
      case 'increment':
        cartChange(btn.dataset.key, parseInt(btn.dataset.qty, 10));
        break;
      case 'upsell-add': {
        const card = btn.closest('.ss-upsell-card');
        const vid = getSelectedUpsellVariant(card) || btn.dataset.variantId;
        cartAdd(vid, 1);
        break;
      }
      case 'rewards-add':
        addRewardsProduct();
        break;
      case 'rewards-without':
        removeRewardsProduct().then(function () { window.location.href = '/checkout'; });
        break;
    }
  }

  function getSelectedUpsellVariant(card) {
    const btn = card && card.querySelector('.ss-upsell-card__variant-btn--selected');
    if (btn) return btn.dataset.variantId;
    const sel = card && card.querySelector('.ss-upsell-card__variant-select');
    if (sel) return sel.value;
    return null;
  }

  /* ── Change delegation (rewards toggle, upsell variant select) ──────────── */
  function handleChange(e) {
    if (!drawer) return;

    const toggle = e.target.closest('#SsRewardsToggle');
    if (toggle && drawer.contains(toggle)) {
      if (toggle.checked) {
        addRewardsProduct();
      } else {
        removeRewardsProduct();
      }
      return;
    }

    const sel = e.target.closest('.ss-upsell-card__variant-select');
    if (sel && drawer.contains(sel)) {
      const card = sel.closest('.ss-upsell-card');
      const addBtn = card && card.querySelector('[data-action="upsell-add"]');
      if (addBtn) addBtn.dataset.variantId = sel.value;
    }
  }

  /* ── Form submit interception ───────────────────────────────────────────── */
  function handleFormSubmit(e) {
    const form = e.target;
    if (!form || form.getAttribute('action') === null) return;
    const action = form.getAttribute('action');
    if (action.indexOf('/cart/add') === -1) return;

    // Stop other listeners on this submit event from firing. Critical because
    // Easy Bundle's submit handler will otherwise navigate to /cart as a
    // fallback when its bundle expansion can't reconcile our async cart add.
    // See the comment block where this listener is registered.
    e.preventDefault();
    e.stopImmediatePropagation();

    const formData = new FormData(form);
    // FormData → URLSearchParams for application/x-www-form-urlencoded
    const params = new URLSearchParams();
    formData.forEach(function (val, key) { params.append(key, val); });
    params.append('sections', SECTION_ID + ',' + POPUP_SECTION_ID);

    setLoading(true);
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: params.toString()
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.status && data.status >= 400) {
          console.error('[ss-cart-drawer] add error:', data.description);
          setLoading(false);
          return;
        }
        updateContents(data.sections && data.sections[SECTION_ID]);
        syncPopupCartState(data.sections);
        announce('Item added to cart.');
        openDrawer();
      })
      .catch(function (err) { console.error('[ss-cart-drawer]', err); setLoading(false); });
  }

  /* ── Cart API helpers ───────────────────────────────────────────────────── */
  function cartAdd(variantId, qty) {
    setLoading(true);
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: parseInt(variantId, 10), quantity: qty, sections: [SECTION_ID, POPUP_SECTION_ID] })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.status && data.status >= 400) {
          console.error('[ss-cart-drawer] add error:', data.description);
          setLoading(false);
          return data;
        }
        updateContents(data.sections && data.sections[SECTION_ID]);
        syncPopupCartState(data.sections);
        announce('Item added to cart.');
        openDrawer();
        return data;
      })
      .catch(function (err) { console.error('[ss-cart-drawer]', err); setLoading(false); });
  }

  function cartChange(key, qty) {
    setLoading(true);
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: key, quantity: qty, sections: [SECTION_ID, POPUP_SECTION_ID] })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        updateContents(data.sections && data.sections[SECTION_ID]);
        syncPopupCartState(data.sections);
        announce('Cart updated.');
        return data;
      })
      .catch(function (err) { console.error('[ss-cart-drawer]', err); setLoading(false); });
  }

  function refreshCart() {
    setLoading(true);
    return fetch('/?sections=' + SECTION_ID + ',' + POPUP_SECTION_ID)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        updateContents(data[SECTION_ID]);
        syncPopupCartState(data);
      })
      .catch(function (err) { console.error('[ss-cart-drawer]', err); setLoading(false); });
  }

  /* ── Rewards helpers ────────────────────────────────────────────────────── */
  function getRewardsVariantId() {
    return drawer ? parseInt(drawer.dataset.rewardsVariant, 10) : null;
  }

  function addRewardsProduct() {
    // Idempotent guard — if rewards is already in cart per the latest
    // server-rendered state, skip the add. /cart/add.js with quantity:1
    // INCREMENTS an existing line by 1 rather than setting it to 1, so a
    // duplicate fire (event re-entry, double-click, etc.) would otherwise
    // stack the rewards product to qty 2+.
    const contents = contentsEl();
    if (contents && contents.dataset.rewardsInCart === 'true') {
      return Promise.resolve();
    }
    const vid = getRewardsVariantId();
    if (!vid) return Promise.resolve();
    // Do the add directly here rather than going through cartAdd(), because
    // cartAdd() ends with openDrawer() — appropriate for user-initiated
    // line-item adds (cart icon, upsell-add button) but NOT for the
    // applyRewardsDefault() path, which fires silently on page init. Routing
    // through cartAdd() caused the drawer to auto-slide-open on the first
    // page load of every new session (the rewards-service product is hidden
    // from the visible item list, so the drawer looked empty too).
    // Mirrors the pattern used by removeRewardsProduct() below.
    setLoading(true);
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: parseInt(vid, 10), quantity: 1, sections: SECTION_ID })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.status && data.status >= 400) {
          console.error('[ss-cart-drawer] rewards add error:', data.description);
          setLoading(false);
          return data;
        }
        updateContents(data.sections && data.sections[SECTION_ID]);
        return data;
      })
      .catch(function (err) { console.error('[ss-cart-drawer]', err); setLoading(false); });
  }

  function getRewardsLineKey() {
    var contents = contentsEl();
    return contents ? (contents.dataset.rewardsLineKey || '') : '';
  }

  function removeRewardsProduct() {
    // Prefer the line item key — /cart/change.js with a variant id can be
    // ambiguous when the variant appears on multiple lines (e.g., split by
    // line item properties or automatic discounts). Fall back to variant id
    // if for some reason the key isn't available.
    const key = getRewardsLineKey();
    const vid = getRewardsVariantId();
    const idForRemoval = key || vid;
    if (!idForRemoval) return Promise.resolve();
    setLoading(true);
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: idForRemoval, quantity: 0, sections: SECTION_ID })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        updateContents(data.sections && data.sections[SECTION_ID]);
        return data;
      })
      .catch(function (err) { console.error('[ss-cart-drawer]', err); setLoading(false); });
  }

  function applyRewardsDefault() {
    if (!drawer) return;
    if (drawer.dataset.rewardsEnabled !== 'true') return;
    if (drawer.dataset.rewardsDefault !== 'opted-in') return;
    if (sessionStorage.getItem(REWARDS_DEFAULT_APPLIED_KEY)) return;

    sessionStorage.setItem(REWARDS_DEFAULT_APPLIED_KEY, 'true');

    const contents = contentsEl();
    if (contents && contents.dataset.rewardsInCart === 'true') return;

    addRewardsProduct();
  }

  /* ── DOM update ─────────────────────────────────────────────────────────── */
  function updateContents(sectionHtml) {
    if (!sectionHtml) { setLoading(false); return; }

    const current = contentsEl();
    if (!current) { setLoading(false); return; }

    const tmp = document.createElement('div');
    tmp.innerHTML = sectionHtml;
    const fresh = tmp.querySelector('#SsCartDrawerContents');
    if (!fresh) { setLoading(false); return; }

    current.replaceWith(fresh);

    // Update header count
    const countSpan = document.getElementById('SsCartCount');
    const newCount = fresh.dataset.itemCount || '0';
    if (countSpan) countSpan.textContent = newCount;

    // Update header cart-icon bubble
    document.querySelectorAll('.cart-link__bubble').forEach(function (el) {
      el.classList.toggle('cart-link__bubble--visible', parseInt(newCount, 10) > 0);
    });

    // Progress bar confetti check
    checkConfetti(fresh);

    // Second-tier auto-add/remove
    handleSecondTier(fresh);

    // Re-apply rewards default to newly rendered drawer if needed
    applyRewardsDefault();

    setLoading(false);

    if (window.lazyLoadInstance) window.lazyLoadInstance.update();
  }

  /* ── Progress bar confetti ──────────────────────────────────────────────── */
  function checkConfetti(el) {
    var bar = el.querySelector('[data-progress-tier]');
    if (!bar) {
      console.log('[ss-cart-drawer] confetti: no progress bar element');
      return;
    }

    var tier = bar.dataset.progressTier;
    var pct = parseFloat(bar.dataset.progressPct || '0');
    console.log('[ss-cart-drawer] confetti check — tier:', tier, 'pct:', pct);

    if (tier === '1' && pct >= 100 && !sessionStorage.getItem(CONFETTI_TIER1_KEY)) {
      // Set the dedupe flag inside fireConfetti's success callback so a
      // silent failure (CDN blocked, canvas size weird, etc.) doesn't
      // permanently disable confetti for this session.
      fireConfetti(CONFETTI_TIER1_KEY);
    } else {
      console.log('[ss-cart-drawer] confetti tier1 skipped — already shown:', !!sessionStorage.getItem(CONFETTI_TIER1_KEY), 'pct >= 100:', pct >= 100);
    }
    if (tier === '2' && pct >= 100 && !sessionStorage.getItem(CONFETTI_TIER2_KEY)) {
      fireConfetti(CONFETTI_TIER2_KEY);
    }
  }

  /* ── Second-tier gift product auto-add/remove ───────────────────────────── */
  function handleSecondTier(el) {
    var dataEl = el.querySelector('[data-second-tier]');
    if (!dataEl) return;

    var variantId = parseInt(dataEl.dataset.tier2VariantId, 10);
    var reached = dataEl.dataset.tier2Reached === 'true';
    var inCart = dataEl.dataset.tier2InCart === 'true';
    if (!variantId) return;

    if (reached && !inCart) {
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1, sections: SECTION_ID })
      })
        .then(function (r) { return r.json(); })
        .then(function (data) { updateContents(data.sections && data.sections[SECTION_ID]); });
    } else if (!reached && inCart) {
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 0, sections: SECTION_ID })
      })
        .then(function (r) { return r.json(); })
        .then(function (data) { updateContents(data.sections && data.sections[SECTION_ID]); });
    }
  }

  /* ── Confetti (lazy-loads canvas-confetti on first fire, scoped to the
        drawer's own canvas so the animation stays inside the drawer panel) ── */
  let _scopedConfetti = null;
  const CONFETTI_OPTS = { particleCount: 120, spread: 80, origin: { y: 0.55 } };

  // Returns true if confetti was actually fired, false otherwise. Caller
  // (fireConfetti) uses this to decide whether to record the dedupe flag —
  // we don't want a silent failure to permanently disable confetti for the
  // session.
  function _runConfetti() {
    console.log('[ss-cart-drawer] _runConfetti: window.confetti =', typeof window.confetti, ', _scopedConfetti =', !!_scopedConfetti);
    if (_scopedConfetti) {
      _scopedConfetti(CONFETTI_OPTS);
      return true;
    }
    var canvas = document.getElementById('SsConfettiCanvas');
    if (!canvas) {
      console.warn('[ss-cart-drawer] confetti: canvas #SsConfettiCanvas not in DOM');
      return false;
    }
    if (typeof window.confetti !== 'function') {
      console.warn('[ss-cart-drawer] confetti: window.confetti is not a function — library failed to load');
      return false;
    }
    // Force explicit canvas image dimensions before creating the scoped
    // instance. canvas-confetti's `resize: true` should handle this, but a
    // 0x0 canvas at measure time produces an invisible fire.
    var w = canvas.offsetWidth || (canvas.parentElement && canvas.parentElement.offsetWidth) || 420;
    var h = canvas.offsetHeight || (canvas.parentElement && canvas.parentElement.offsetHeight) || window.innerHeight;
    canvas.width = w;
    canvas.height = h;
    console.log('[ss-cart-drawer] confetti: canvas size set to', w, 'x', h);
    if (typeof window.confetti.create === 'function') {
      _scopedConfetti = window.confetti.create(canvas, { resize: true });
      _scopedConfetti(CONFETTI_OPTS);
      console.log('[ss-cart-drawer] confetti: fired (scoped)');
      return true;
    }
    // Library doesn't expose .create on this version — fall back to global
    // (full-screen) confetti so the celebration at least fires somewhere.
    console.warn('[ss-cart-drawer] confetti.create unavailable — falling back to fullscreen');
    window.confetti(CONFETTI_OPTS);
    return true;
  }

  function fireConfetti(dedupeKeyToSetOnSuccess) {
    function tryAndRecord() {
      var fired = _runConfetti();
      if (fired && dedupeKeyToSetOnSuccess) {
        sessionStorage.setItem(dedupeKeyToSetOnSuccess, 'true');
      }
    }
    if (typeof window.confetti === 'function') {
      tryAndRecord();
      return;
    }
    console.log('[ss-cart-drawer] confetti: loading library from CDN');
    var s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/canvas-confetti@1.9.3/dist/confetti.browser.min.js';
    s.onload = function () {
      console.log('[ss-cart-drawer] confetti: library loaded');
      tryAndRecord();
    };
    s.onerror = function () {
      console.warn('[ss-cart-drawer] confetti: script failed to load (CDN blocked? CSP? network?)');
    };
    document.head.appendChild(s);
  }

  /* ── Loading state ──────────────────────────────────────────────────────── */
  function setLoading(on) {
    if (!drawer) return;
    drawer.classList.toggle('ss-cart-drawer--loading', on);
  }

  /* ── Screen-reader announcement ─────────────────────────────────────────── */
  function announce(msg) {
    if (!announcerEl) return;
    announcerEl.textContent = '';
    requestAnimationFrame(function () { announcerEl.textContent = msg; });
  }

  /* ── ?cart=open ─────────────────────────────────────────────────────────── */
  function handleUrlParam() {
    if (new URLSearchParams(window.location.search).get('cart') === 'open') openDrawer();
  }

  /* ── Boot ───────────────────────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ── Public API ─────────────────────────────────────────────────────────── */
  /* Exposed for cross-section triggering — e.g., the future ss-popup-upsell
   * module (Phase 3) will call window.SsCartDrawer.open() after the customer
   * accepts/declines the post-add modal. The drawer's own form-submit
   * interceptor keeps DOM in sync when things are added via the cart-icon
   * flow, but external callers that hit /cart/add.js directly need to call
   * SsCartDrawer.refresh() afterward to re-render the drawer's section HTML
   * — otherwise the drawer DOM stays stale until the next full page load. */
  window.SsCartDrawer = window.SsCartDrawer || {};

  // Refresh the drawer's rendered section HTML from the server. Returns a
  // Promise that resolves when the DOM has been updated.
  window.SsCartDrawer.refresh = function () {
    if (!drawer) return Promise.resolve();
    return refreshCart();
  };

  // Open the drawer. Refreshes the drawer's contents first so any cart
  // mutations made outside this drawer's own interceptor (e.g., box builder
  // adds) are reflected before the customer sees the slide-in.
  window.SsCartDrawer.open = function () {
    function doOpen() {
      // Fire refresh and open in parallel — the drawer slides in immediately
      // showing whatever is currently rendered, and contents update in place
      // when the section fetch lands. This avoids a perceptible delay before
      // the drawer appears, matching the behavior the cart-icon path provides.
      refreshCart();
      openDrawer();
    }
    if (drawer) {
      doOpen();
    } else {
      // Drawer not yet initialized (DOMContentLoaded hasn't fired) — defer.
      document.addEventListener('DOMContentLoaded', function once() {
        document.removeEventListener('DOMContentLoaded', once);
        if (drawer) doOpen();
      });
    }
  };

  window.SsCartDrawer.close = function () { if (drawer) closeDrawer(); };
})();